import React from "react";
import {
  CheckCircle2,
  Clock3,
  FoldHorizontal,
  Move3D,
  Network,
  RefreshCcw,
  RotateCcw,
  Search,
  Spline,
  Target,
  Trophy,
  UnfoldHorizontal,
  Waypoints,
  XCircle,
  GitBranch,
} from "lucide-react";
import {
  SCENE_GRAPH_NODE_RADIUS,
  sceneGraphNodeFillStyle,
  sceneGraphIconSize,
} from "../../utils/sceneGraphNodeAppearance";

/** Scales scene-graph radii so behavior nodes are easier to see and hit in the editor. */
export const BT_NODE_RADIUS_SCALE = 1.55;

/** Maps behavior categories to Scene Graph node kinds — identical palettes & radii. */
export const BT_CATEGORY_SCENE_TYPE = {
  composite: "site",
  motion: "inertial",
  gripper: "gripper",
  scene: "camera",
  condition: "mesh",
  episode: "joint",
  default: "tendon",
};

export function getCategory(kind) {
  if (
    kind === "sequence" ||
    kind === "selector" ||
    kind === "retry" ||
    kind === "repeat" ||
    kind === "ifThenElse" ||
    kind === "templateIfThenElse"
  )
    return "composite";
  if (
    kind === "moveEeToXyz" ||
    kind === "moveEeByOffset" ||
    kind === "goToKeyframe" ||
    kind === "servoPickPlace" ||
    kind === "runTrainedModel"
  )
    return "motion";
  if (kind === "openGripper" || kind === "closeGripper") return "gripper";
  if (
    kind === "randomizeObjectPose" ||
    kind === "setObjectPose" ||
    kind === "resetToBaseline" ||
    kind === "wait"
  )
    return "scene";
  if (
    kind === "conditionObjectGrasped" ||
    kind === "conditionEeNear" ||
    kind === "conditionObjectNear" ||
    kind === "conditionTimeElapsed"
  )
    return "condition";
  if (kind === "markSuccess" || kind === "markFailure") return "episode";
  return "default";
}

export function getKindIcon(kind) {
  switch (kind) {
    case "sequence":
      return Spline;
    case "selector":
      return Waypoints;
    case "ifThenElse":
    case "templateIfThenElse":
      return GitBranch;
    case "retry":
      return RotateCcw;
    case "repeat":
      return RefreshCcw;
    case "servoPickPlace":
      return Target;
    case "moveEeToXyz":
      return Move3D;
    case "moveEeByOffset":
      return Network;
    case "runTrainedModel":
      return Network;
    case "openGripper":
      return UnfoldHorizontal;
    case "closeGripper":
      return FoldHorizontal;
    case "goToKeyframe":
      return Trophy;
    case "wait":
      return Clock3;
    case "randomizeObjectPose":
      return RefreshCcw;
    case "setObjectPose":
      return Target;
    case "resetToBaseline":
      return RotateCcw;
    case "markSuccess":
      return CheckCircle2;
    case "markFailure":
      return XCircle;
    case "conditionObjectGrasped":
    case "conditionEeNear":
    case "conditionObjectNear":
    case "conditionTimeElapsed":
      return Search;
    default:
      return Target;
  }
}

/** idle → muted, running → amber, success → emerald, failure → rose */
export const STATUS_COLORS = {
  idle: "#52525b",
  running: "#fbbf24",
  success: "#34d399",
  failure: "#f87171",
};

/** Category accent used in inspector headers and popover group labels. */
export const CATEGORY_COLORS = {
  composite: "#a78bfa",
  templates: "#a78bfa",
  motion: "#60a5fa",
  gripper: "#0ea5e9",
  scene: "#34d399",
  condition: "#22c55e",
  episode: "#facc15",
  default: "#9ca3af",
};

/**
 * Mini disc + icon matching the scene-graph disc style for a given behavior
 * `kind`. Used inside leaves, palette popover rows, and composite headers.
 */
export function BtNodeDisc({ kind, size = 26, largeIcon = false }) {
  const category = getCategory(kind);
  const sceneType = BT_CATEGORY_SCENE_TYPE[category] ?? BT_CATEGORY_SCENE_TYPE.default;
  const baseR = SCENE_GRAPH_NODE_RADIUS[sceneType] ?? 10;
  const r = baseR * BT_NODE_RADIUS_SCALE;
  const Icon = getKindIcon(kind);
  const { fill, stroke, strokeWidth: sw } = sceneGraphNodeFillStyle(sceneType, false, false);
  const icoSize = sceneGraphIconSize(r);
  const iconFrac = largeIcon ? 0.72 : 0.55;
  const iconCap = largeIcon ? 28 : 22;
  const iconPx = Math.max(12, Math.min(iconCap, Math.round(icoSize * iconFrac)));
  const vbPad = 8;
  const vb = `${-r - vbPad} ${-r - vbPad} ${2 * r + 2 * vbPad} ${2 * r + 2 * vbPad}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={vb}
      className="block shrink-0 overflow-visible pointer-events-none"
      aria-hidden
    >
      <circle cx={0} cy={0} r={r} fill={fill} stroke={stroke} strokeWidth={sw} />
      <foreignObject
        x={-icoSize / 2}
        y={-icoSize / 2}
        width={icoSize}
        height={icoSize}
        style={{ pointerEvents: "none" }}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className="flex h-full w-full items-center justify-center text-white"
        >
          <Icon width={iconPx} height={iconPx} strokeWidth={2} aria-hidden />
        </div>
      </foreignObject>
    </svg>
  );
}
